/**
 * ProjectAccessView — 2026-09-25 (user request): mode "Per Project" pada panel
 * Akses Client & Project. Pilih satu project induk → tampil semua user yang
 * punya akses (atau eksplisit ditolak) beserta divisi/level/jabatan, sumber
 * akses (Super Admin / Divisi & Level / Client Access / Project Grant /
 * User Override / Warisan WO-VO), status, dan fitur yang dapat dilihat.
 *
 * Catatan: daftar project HANYA project induk (bukan WO/VO, bukan internal) —
 * sesuai ketentuan user bahwa WO/VO tidak ikut dihitung sebagai project induk.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FolderKanban, Users as UsersIcon, Ban } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { DIVISION_COLOR } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import SearchableSelect from "@/components/SearchableSelect";

const SOURCE_LABELS = {
  super_admin: "Super Admin (BOD Lv1)",
  matrix_view_all: "Matrix: Lihat Semua Proyek",
  div_level: "Divisi & Level",
  client_access: "Client Access",
  project_grant: "Project Grant",
  user_override: "User Override",
  inherit_child: "Warisan WO/VO",
  inherit_parent: "Warisan Induk",
};
const SOURCE_CLS = {
  super_admin: "bg-[#0B1D3A] text-white border-[#0B1D3A]",
  matrix_view_all: "bg-indigo-50 text-indigo-700 border-indigo-300",
  div_level: "bg-sky-50 text-sky-700 border-sky-300",
  client_access: "bg-violet-50 text-violet-700 border-violet-300",
  project_grant: "bg-amber-50 text-amber-700 border-amber-300",
  user_override: "bg-orange-50 text-orange-700 border-orange-300",
  inherit_child: "bg-slate-100 text-slate-600 border-slate-300",
  inherit_parent: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function ProjectAccessView({ clients }) {
  // Hanya project induk: tanpa parent_project_id dan bukan internal.
  const mainProjects = useMemo(() => {
    const out = [];
    for (const c of clients || []) {
      for (const p of c.projects || []) {
        if (!p.parent_project_id && p.type !== "internal") {
          out.push({ id: p.id, name: p.name, client: c.client_name, status: p.status });
        }
      }
    }
    out.sort((a, b) => (a.client || "").localeCompare(b.client || "") || (a.name || "").localeCompare(b.name || ""));
    return out;
  }, [clients]);

  const [projectId, setProjectId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const { user } = useAuth();
  const { canPerm } = useAccess();
  // Quick-revoke butuh perm yang sama dgn pengelolaan matrix: access_control.update.
  const canManage = (user?.division === "BOD" && Number(user?.level) === 1) || canPerm(user, "access_control", "update");

  // 2026-09-25 (user request): Cabut akses cepat per baris — buat User Override
  // "ditolak" untuk project ini tanpa pindah mode. Super Admin tidak bisa dicabut
  // dari sini (aksesnya dari matrix view_all_projects, bukan grant project).
  const revokeAccess = async (r) => {
    if (!window.confirm(
      `Cabut akses ${r.name} dari project "${data?.project?.name || ""}"?\n\n` +
      `Sistem membuat User Override "ditolak" untuk project ini. Sumber akses aslinya (${SOURCE_LABELS[r.source] || r.source}) tidak dihapus. Aksi tercatat di audit log.`
    )) return;
    setBusyId(r.user_id);
    try {
      await api.patch("/project-access-overrides", { project_id: projectId, user_id: r.user_id, granted: false });
      toast.success(`Akses ${r.name} dicabut untuk project ini.`);
      const { data: d2 } = await api.get("/client-access/project-users", { params: { project_id: projectId } });
      setData(d2);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal mencabut akses.");
    } finally { setBusyId(""); }
  };

  useEffect(() => {
    if (!projectId) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/client-access/project-users", { params: { project_id: projectId } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat akses project");
          setData(null);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const rows = data?.users || [];
  const allowedCount = rows.filter((r) => r.status === "allowed").length;
  const deniedCount = rows.filter((r) => r.status === "denied").length;

  return (
    <div data-testid="ca-project-view">
      {/* Selector project induk */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 flex-wrap">
        <FolderKanban size={13} className="text-slate-500" />
        {/* 2026-10-01 (user request): dropdown project induk jadi searchable —
            bisa diketik untuk mencari (konsisten dgn picker COA/petty cash). */}
        <SearchableSelect
          value={projectId}
          onChange={setProjectId}
          options={mainProjects.map((p) => ({ value: p.id, label: `${p.name} — ${p.client}` }))}
          placeholder="Pilih project induk…"
          searchPlaceholder="Ketik nama project / client…"
          emptyText="Project induk tidak ditemukan"
          testid="ca-project-select"
        />
        <span className="text-[10px] tabular-nums text-slate-500" data-testid="ca-project-count">
          {mainProjects.length} project induk <span className="italic">(WO/VO tidak dihitung)</span>
        </span>
      </div>

      {/* Hasil */}
      {!projectId ? (
        <div className="p-8 text-center text-xs text-slate-400 italic" data-testid="ca-pv-empty">
          Pilih project induk untuk melihat siapa saja yang memiliki akses.
        </div>
      ) : loading ? (
        <div className="p-8 text-center text-xs text-slate-400 italic">Memuat akses project…</div>
      ) : !data ? null : (
        <>
          <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#0B1D3A] truncate" data-testid="ca-pv-project-name">{data.project.name}</div>
              <div className="text-[10px] text-slate-500">
                {data.project.client || "(tanpa client)"} · {data.project.type}
                {data.project.child_count > 0 && ` · ${data.project.child_count} WO/VO di bawahnya`}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 text-[10px] tabular-nums">
              <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-300 font-semibold" data-testid="ca-pv-allowed-count">
                <UsersIcon size={10} className="inline mr-1" />{allowedCount} diizinkan
              </span>
              {deniedCount > 0 && (
                <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-300 font-semibold" data-testid="ca-pv-denied-count">{deniedCount} ditolak</span>
              )}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left overline text-slate-600 text-[10px]">
                <th className="px-3 py-2">Nama</th>
                <th className="px-3 py-2">Divisi</th>
                <th className="px-3 py-2">Lv</th>
                <th className="px-3 py-2">Jabatan</th>
                <th className="px-3 py-2">Sumber Akses</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Fitur Dapat Dilihat</th>
                <th className="px-3 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody data-testid="ca-pv-table">
              {rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-xs text-slate-400 italic">Tidak ada user yang memiliki akses ke project ini.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ca-pv-row-${r.user_id}`}>
                  <td className="px-3 py-2 font-semibold text-[#0B1D3A]">{r.name}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[r.division] || "bg-slate-600 text-white"}`}>{r.division}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">Lv{r.level}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.sub_division || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${SOURCE_CLS[r.source] || "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid={`ca-pv-src-${r.user_id}`}>
                      {SOURCE_LABELS[r.source] || r.source}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {r.status === "allowed" ? (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-300" data-testid={`ca-pv-status-${r.user_id}`}>Diizinkan</span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-300" data-testid={`ca-pv-status-${r.user_id}`}>Ditolak</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1" data-testid={`ca-pv-features-${r.user_id}`}>
                      {(r.features || []).map((f) => (
                        <span key={f} className="px-1 py-0.5 text-[9px] bg-slate-100 text-slate-600 border border-slate-200">{f}</span>
                      ))}
                      {(r.features || []).length === 0 && <span className="text-[10px] text-slate-400 italic">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.status === "allowed" && r.source !== "super_admin" && canManage ? (
                      <button type="button"
                        onClick={() => revokeAccess(r)}
                        disabled={busyId === r.user_id}
                        data-testid={`ca-pv-revoke-${r.user_id}`}
                        title={r.source === "matrix_view_all"
                          ? "Cabut per project: buat User Override 'ditolak' — matrix view_all_projects tidak diubah, tapi user tidak akan lihat project ini."
                          : "Buat User Override 'ditolak' untuk project ini"}
                        className="inline-flex items-center gap-1 px-2 py-0.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[9px] uppercase font-bold disabled:opacity-40">
                        <Ban size={10} /> {busyId === r.user_id ? "…" : "Cabut Akses"}
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-400"
                        title={r.source === "super_admin" ? "BOD Lv1 (Super Admin) tidak bisa dicabut dari sini — aksesnya mutlak" : ""}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
